import type { SubscriptionResponse } from '@cvo/domain/api';
import type { SubscriptionDisplayStatus } from '@cvo/domain/api/models/bff/Subscription';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import type { SupportedStatusColor } from '@cvo/ui/components/atoms/Card/Card.model';
import { useDateFormat } from './useDateFormat';
import { useSubscription } from './useSubscription';

export interface StatusLabel {
  status: SupportedStatusColor;
  text: string;
  disabled?: boolean;
}

export const statusMap: Record<SubscriptionDisplayStatus, StatusLabel | null> = {
  PENDING_ACTIVATION: {
    status: 'info',
    text: 'ui.subscription.details.status.pendingActivation',
  },
  ACTIVE: {
    status: 'success',
    text: 'ui.subscription.details.status.active',
  },
  PENDING_RENEWAL: {
    status: 'info',
    text: 'ui.subscription.details.status.pendingRenewal',
  },
  OVERDUE: {
    status: 'danger',
    text: 'ui.subscription.details.status.overdue',
  },
  SUSPENDED: {
    status: 'danger',
    text: 'ui.subscription.details.status.suspended',
  },
  CANCELLATION_REQUESTED: {
    status: 'success',
    text: 'ui.subscription.details.status.active',
  },
  CANCELLED: {
    disabled: true,
    status: 'inactive',
    text: 'ui.subscription.details.status.cancelled',
  },
};

export function useSubscriptionStatus(subscription: MaybeRefOrGetter<SubscriptionResponse | null | undefined>) {
  const { t } = useI18n();
  const { startDate } = useSubscription(subscription);

  const startDateFormatted = useDateFormat(startDate, { dateFormat: 'long', fallback: '-' });

  const statusData = computed(() => {
    const subscriptionValue = toValue(subscription);
    if (!subscriptionValue) return null;
    return statusMap[subscriptionValue.displayStatus] ?? null;
  });

  const cardStatusColor = computed<SupportedStatusColor | undefined>(() => {
    return statusData.value?.status;
  });

  const cardStatusText = computed<string | undefined>(() => {
    const key = statusData.value?.text ?? '';
    if (!key) return undefined;

    return t(key, {
      startDate: startDateFormatted.value,
    });
  });

  const isCardDisabled = computed(() => statusData.value?.disabled ?? false);

  return {
    cardStatusColor,
    cardStatusText,
    isCardDisabled,
  };
}
